import React, { memo } from 'react';

// Memoize the skeleton component to prevent re-rendering when parent components re-render
const DashboardSkeleton: React.FC = memo(
  () => {
    return (
      <div className="h-full overflow-hidden flex flex-col">
        <div className="p-4 w-full">
          <div className="w-full flex justify-between items-center">
            {/* Skeleton header with personalized greeting */}
            <div className="h-8 bg-gray-200 rounded-md w-96 relative overflow-hidden">
              <div className="animate-shimmer absolute inset-0"></div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <div className="w-32 h-8 bg-gray-200 rounded-full relative overflow-hidden">
                <div className="animate-shimmer absolute inset-0"></div>
              </div>
              <div className="w-28 h-8 bg-gray-200 rounded-full relative overflow-hidden">
                <div className="animate-shimmer absolute inset-0"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Task columns */}
        <div className="flex-1 overflow-hidden p-4 w-full">
          <div className="w-full h-full">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 h-full max-h-full">
              {/* Generate 4 columns with skeleton task cards */}
              {[...Array(4)].map((_, columnIndex) => (
                <div key={columnIndex} className="flex flex-col h-full">
                  {/* Column header with icon and title */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 bg-gray-200 rounded-lg w-8 h-8 relative overflow-hidden">
                      <div className="animate-shimmer absolute inset-0"></div>
                    </div>
                    <div className="flex items-center">
                      <div className="h-6 bg-gray-200 rounded-md w-28 relative overflow-hidden">
                        <div className="animate-shimmer absolute inset-0"></div>
                      </div>
                      <div className="ml-2 h-5 w-8 bg-gray-200 rounded-full relative overflow-hidden">
                        <div className="animate-shimmer absolute inset-0"></div>
                      </div>
                    </div>
                  </div>

                  {/* Container for skeleton task cards */}
                  <div className="flex-1 overflow-hidden">
                    <div className="space-y-3 overflow-y-auto pr-1 h-full max-h-[calc(100vh-180px)] relative">
                      {/* Generate 3-5 skeleton task cards per column, randomly sized */}
                      {[...Array(Math.floor(Math.random() * 3) + 3)].map((_, cardIndex) => {
                        // Randomize content variations for more realistic appearance
                        const hasTags = Math.random() > 0.5;
                        const hasDesc = Math.random() > 0.3;

                        return (
                          <div
                            key={cardIndex}
                            className={`p-4 bg-white border-l-[5px] border-gray-200 rounded-lg mb-3 shadow-sm
                            ${['border-l-red-300', 'border-l-green-300', 'border-l-blue-300', 'border-l-yellow-300'][columnIndex]}`}
                          >
                            {/* Project/topic info */}
                            <div className="h-3 bg-gray-200 rounded w-1/2 mb-3 relative overflow-hidden">
                              <div className="animate-shimmer absolute inset-0"></div>
                            </div>

                            {/* Task title */}
                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 relative overflow-hidden">
                              <div className="animate-shimmer absolute inset-0"></div>
                            </div>

                            {/* Task description (sometimes) */}
                            {hasDesc && (
                              <div className="space-y-1 mb-3">
                                <div className="h-3 bg-gray-200 rounded w-full relative overflow-hidden">
                                  <div className="animate-shimmer absolute inset-0"></div>
                                </div>
                                <div className="h-3 bg-gray-200 rounded w-5/6 relative overflow-hidden">
                                  <div className="animate-shimmer absolute inset-0"></div>
                                </div>
                              </div>
                            )}

                            {/* Deadline indicator */}
                            <div className="h-4 bg-gray-200 rounded w-28 mb-2 flex items-center relative overflow-hidden">
                              <div className="h-3 w-3 bg-gray-300 rounded-full mr-2 relative overflow-hidden">
                                <div className="animate-shimmer absolute inset-0"></div>
                              </div>
                              <div className="animate-shimmer absolute inset-0"></div>
                            </div>

                            {/* Tags (sometimes) */}
                            {hasTags && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                <div className="h-4 bg-gray-200 rounded-full w-12 relative overflow-hidden">
                                  <div className="animate-shimmer absolute inset-0"></div>
                                </div>
                                <div className="h-4 bg-gray-200 rounded-full w-16 relative overflow-hidden">
                                  <div className="animate-shimmer absolute inset-0"></div>
                                </div>
                                {Math.random() > 0.5 && (
                                  <div className="h-4 bg-gray-200 rounded-full w-14 relative overflow-hidden">
                                    <div className="animate-shimmer absolute inset-0"></div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
  () => true
); // Always return true from memo comparison to prevent re-rendering

export default DashboardSkeleton;
